import {
  CanvasArgs,
  CanvasOpType,
  FsId,
  RawEventArgs,
  RawEventData,
  RecFeature,
} from '@fullstory/recording-api';
import { renderMasked } from '@fullstory/shared/masktext';
import { DirtyBit } from './dirtybits';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';

export class FsCanvas extends FsNode {
  private _isScreenShotMode = false;
  constructor(doc: FsDocument, id?: FsId) {
    super(doc, 'canvas', undefined, id);
    if (doc.recFeatures.get(RecFeature.CanvasWatcherEnabled)) {
      this.setAttr('_fs_canvas', '1', true);
    }
    this._isScreenShotMode = !!doc.recFeatures.get(RecFeature.CanvasScreenShotMode);
  }
  private _ops: RawEventArgs['CANVAS'][] = [];

  event(event: RawEventData<'CANVAS'>) {
    this.setDirtyBit(DirtyBit.Canvas);
    this._scanForImages(event.Args);
    this._ops.push(event.Args);
  }

  flush(): void {
    super.flush();
    if (this._hasDirtyBit(DirtyBit.Canvas)) {
      this._flushCanvasOps();
      this._clearDirtyBit(DirtyBit.Canvas);
    }
  }

  private _scanForImages(eventArgs: RawEventArgs['CANVAS']) {
    // we need to preload any detached images and tell document to load them ahead of time
    const [_, ops] = eventArgs;
    for (const op of ops) {
      const args = op[3] as any[];
      for (const arg of args) {
        if (arg?.ctor === 'HTMLImageElement' && arg?.src) {
          if (this.ownerDoc.canvasImages.has(arg.src)) {
            continue;
          }
          const elem = new Image();
          elem.src = arg.src;
          const imgLoad = new Promise((resolve, reject) => {
            elem.addEventListener('load', resolve);
            elem.addEventListener('error', reject);
          });
          this.ownerDoc.requiredBeforeFlush.push(imgLoad);
          this.ownerDoc.canvasImages.set(arg.src, elem);
        }
      }
    }
  }

  private _flushCanvasOps() {
    const canvas = this.dom as HTMLCanvasElement;

    /**
     * Set of all contexts used by this canvas
     */
    const contextKeys = new Set<string>(
      this._ops.reduce<string[]>((agg, next) => {
        agg.push(...next[1].map(op => op[0]));
        return agg;
      }, []),
    );

    const contextMap = new Map<
      string,
      | CanvasRenderingContext2D
      | WebGLRenderingContext
      | WebGL2RenderingContext
      | ImageBitmapRenderingContext
    >();
    for (const contextKey of contextKeys) {
      const context = canvas.getContext(contextKey);
      if (!context) {
        console.warn(`Could not get canvas context ${contextKey}`);
        return;
      }
      contextMap.set(contextKey, context);
    }

    for (const event of this._ops) {
      const [_, ops] = event;
      for (const op of ops) {
        const [contextKey, type, key, args, returnId] = op;

        try {
          const unwrappedArgs = unwrapCanvasArgs(this.ownerDoc, args);
          const context = contextMap.get(contextKey);
          if (!context) {
            console.warn(`Could not find a context that should be previously initialized`);
            return;
          }
          const desc = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(context), key);
          if (!desc) {
            console.warn(
              `Could not retrieve property descriptor ${key} for canvas context ${contextKey}`,
            );
            continue;
          }

          if (type === CanvasOpType.Set) {
            if (typeof desc.set !== 'function') {
              console.warn(
                `Attempted to set attribute ${key} on canvas context but descriptor claims there is no setter; possible capture bug or browser feature mismatch`,
              );
              continue;
            }
            desc.set.call(context, unwrappedArgs[0]);
          } else {
            if (typeof desc.value !== 'function') {
              console.warn(
                `Attempted to call function ${key} on canvas context but descriptor claims its value is not a function; possible capture bug or browser feature mismatch`,
              );
              continue;
            }
            if (this._isScreenShotMode && contextKey === '2d') {
              (context as CanvasRenderingContext2D).clearRect(0, 0, canvas.width, canvas.height);
            }

            if (key === 'fillText' && this.hasAttr('_fs_masked')) {
              // TODO: find a way to match masked size to original font size
              (context as CanvasRenderingContext2D).font = '25px fswf';
              unwrappedArgs[0] = renderMasked(unwrappedArgs[0]);
            }
            const result = desc.value.apply(context, unwrappedArgs);
            if (returnId) {
              if (typeof result !== 'object') {
                console.warn(
                  `Return value expected: possible capture bug or browser feature mismatch`,
                );
              } else {
                this.ownerDoc.canvasRefs.set(returnId, result);
              }
            }
          }
        } catch (e) {
          console.warn(`failed to run canvas op ${contextKey}#${key}`, e);
        }
      }
    }

    this._ops = [];
  }
}

function unwrapCanvasArgs(ownerDoc: FsDocument, args: CanvasArgs[]): any[] {
  return args.map(arg => unwrapCanvasArg(ownerDoc, arg));
}

function unwrapCanvasArg(ownerDoc: FsDocument, a: CanvasArgs): unknown {
  switch (typeof a) {
    case 'string':
    case 'number':
    case 'boolean':
      return a;
    case 'object':
      if (!a) {
        return a;
      }
      if (Array.isArray(a)) {
        return unwrapCanvasArgs(ownerDoc, a);
      }
      if (a.undef) {
        return undefined;
      }
      if (a.ref) {
        const output = ownerDoc.canvasRefs.get(a.ref);
        if (!output) {
          console.warn(`expected to use ref id ${a.ref} but it's null`);
        }
        return output;
      }
      if (!a.ctor) {
        console.warn(`ctor was not defined on a wrapped argument; possible fs.js bug?`);
        return {};
      }
      if (a.id) {
        return ownerDoc.byId[a.id]?.dom;
      }

      switch (a.ctor) {
        case 'HTMLImageElement': {
          if (a.src && ownerDoc.canvasImages.has(a.src)) {
            return ownerDoc.canvasImages.get(a.src);
          }

          console.warn(`playback did not preload image for ${a.src}`);
          return undefined;
        }
        case 'ImageData': {
          if (!a.obj) {
            console.warn(`missing data for ImageData object`);
            return {};
          }

          // @ts-ignore compiler doesn't seem to like having 4 arguments for some reason
          return new ImageData(unwrapCanvasArg(ownerDoc, a.obj.data), a.obj.width, a.obj.height, {
            colorSpace: a.obj.colorSpace,
          });
        }
        case 'HTMLCanvasElement': {
          const { srcId } = a;
          if (typeof srcId === 'number') {
            return ownerDoc.byId[srcId]?.dom;
          } else if (srcId) {
            const { id } = srcId;
            return ownerDoc.byDetachedCanvasId[id]?.dom;
          } else {
            console.warn('could not unwrap target canvas element');
            return {};
          }
        }
        default:
          if (a.id) {
            return ownerDoc.byId[a.id];
          } else if (a.typedArray) {
            const ctor = window[a.ctor as any] as any;
            if (typeof ctor === 'function') {
              const output = new ctor(JSON.parse(a.typedArray));
              return output;
            } else {
              console.warn(`could not find constructor for ${a.ctor}`);
              return undefined;
            }
          }
          console.warn(`could not unwrap unhandled object type ${a.ctor}`);
          return {};
      }
    default:
      console.warn(`unable to unwrap argument ${a}`);
      return {};
  }
}
